import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories, mainSiteUrl } from "@/mocks/categories";

const languages = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileCatsOpen(false);
    setLangOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const lang = i18n.language?.split("-")[0] || "en";
    document.documentElement.lang = lang;
  }, [i18n.language]);

  const currentLang = languages.find((l) => l.code === i18n.language) ?? languages[0];

  const navLinkClass =
    "text-sm font-semibold transition-colors whitespace-nowrap text-foreground-800 hover:text-primary-600";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/80">
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-grid-line text-base" />
            </span>
            <span className="font-heading font-bold text-lg text-foreground-950 whitespace-nowrap">
              {t("brandName")}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-7">
            <Link to="/" className={navLinkClass}>
              {t("navHome")}
            </Link>
            <Link to="/#guides" className={navLinkClass}>
              {t("navShoppingGuide")}
            </Link>
            <Link to="/#categories" className={navLinkClass}>
              {t("navCategories")}
            </Link>
            <Link to="/#faq" className={navLinkClass}>
              {t("navFaq")}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button
                type="button"
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-foreground-800 cursor-pointer whitespace-nowrap transition-colors hover:bg-background-100"
              >
                <i className="ri-global-line text-base" />
                <span>{currentLang.label}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${langOpen ? "rotate-180" : ""}`} />
              </button>

              {langOpen && (
                <div className="absolute right-0 mt-2 w-44 rounded-lg border border-background-200 bg-background-50 shadow-sm overflow-hidden">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm cursor-pointer whitespace-nowrap transition-colors hover:bg-background-100 ${
                        lang.code === i18n.language
                          ? "text-primary-600 font-semibold"
                          : "text-foreground-700"
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("ctaBuyNow")}
              <i className="ri-arrow-right-up-line text-sm" />
            </a>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-900 cursor-pointer"
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} />
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 px-4 py-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="px-3 py-3 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t("navHome")}
            </Link>
            <Link
              to="/#guides"
              className="px-3 py-3 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t("navShoppingGuide")}
            </Link>
            <Link
              to="/#faq"
              className="px-3 py-3 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t("navFaq")}
            </Link>

            <button
              type="button"
              onClick={() => setMobileCatsOpen((v) => !v)}
              className="flex items-center justify-between px-3 py-3 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <span>{t("navCategories")}</span>
              <i
                className={`ri-arrow-down-s-line text-base transition-transform ${
                  mobileCatsOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {mobileCatsOpen && (
              <div className="grid grid-cols-2 gap-2 px-3 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.href}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-price-tag-3-line text-sm text-foreground-400" />
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              {t("ctaBuyNow")}
              <i className="ri-arrow-right-up-line text-sm" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}